"use client";

import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Capabilities() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = [{
    number: "MD-01",
    price: "Strategy",
    badge: "Foundation",
    name: "Brand",
    accent: "Positioning",
    ingredients: "Defining your unique market voice and competitive edge through rigorous research.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/brand_strategy.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/brand_strategy.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/brand_strategy.png-wvc-sizes",
    alt: "Minimalist architectural shadows on white wall"
  }, {
    number: "MD-02",
    price: "Growth",
    badge: "Execution",
    name: "Digital",
    accent: "Presence",
    ingredients: "Crafting high-conversion digital touchpoints that turn casual visitors into loyalists.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/digital_presence.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/digital_presence.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/digital_presence.png-wvc-sizes",
    alt: "Abstract textured fabric in warm light"
  }, {
    number: "MD-03",
    price: "Creative",
    badge: "Impact",
    name: "Content",
    accent: "Direction",
    ingredients: "Editorial-grade storytelling that commands attention and builds lasting authority.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/content_direction.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/content_direction.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/content_direction.webp-wvc-sizes",
    alt: "Minimalist landscape with negative space"
  }];
  return <section data-section-id="1182" id="core-capabilities" className="relative bg-background text-foreground py-24 md:py-24 overflow-hidden">
      <div className="container mx-auto max-w-[1440px] px-6 md:px-12">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-24 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
      }}>
          <div className="lg:col-span-9 relative">
            <div aria-hidden="true" className="pointer-events-none absolute -top-10 -left-5 md:-top-14 md:-left-6 select-none font-default font-normal leading-none" style={{
            fontSize: "clamp(8rem, 16vw, 14rem)",
            color: "hsl(0 0% 5% / 0.03)"
          }}>
              MD
            </div>

            <div className="relative flex items-center gap-4">
              <span className="font-default text-[0.75rem] font-semibold uppercase tracking-[0.25em]" style={{
              color: "hsl(0 0% 45%)"
            }}>
                MD-03 — CORE CAPABILITIES
              </span>
            </div>

            <div className="relative mt-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <svg width="32" height="14" viewBox="0 0 32 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M2 7 Q 8 1, 16 7 T 30 7" stroke="hsl(21 82% 51%)" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <div className="h-px flex-1 bg-border" />
              <span className="h-1 w-1" style={{
              backgroundColor: "hsl(21 82% 51%)"
            }} />
            </div>

            <h2 className="relative mt-8 font-default font-medium text-foreground" style={{
            fontSize: "clamp(2.25rem, 4.6vw, 4rem)",
            lineHeight: "0.9",
            letterSpacing: "0.15em"
          }}>
              Expertise in{" "}
              <span className={`italic inline-block transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"}`} style={{
              color: "hsl(21 82% 51%)",
              transitionDelay: "200ms"
            }}>
                Action
              </span>
            </h2>
          </div>

          <div className="lg:col-span-3 flex flex-col justify-end items-start lg:items-end lg:text-right gap-5">
            <p className="font-default text-lg" style={{
            color: "hsl(0 0% 45%)",
            lineHeight: "1.6",
            fontWeight: 400
          }}>
              Disciplined marketing frameworks designed to scale your influence.
            </p>
            <Link to="/ServicesPage" className="group inline-flex items-center gap-2 font-default text-sm font-medium text-foreground">
              <span className="relative pb-1" style={{
              textDecoration: "underline",
              textDecorationColor: "hsl(21 82% 51%)",
              textUnderlineOffset: "4px",
              textDecorationThickness: "1px"
            }}>
                View All Services
              </span>
              <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" style={{
              color: "hsl(21 82% 51%)"
            }} />
            </Link>
          </div>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, i) => <article data-index={i} key={i} className={`group relative bg-card border border-border p-8 transition-all duration-300 hover:border-[hsl(21_82%_51%)] ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
          transitionDelay: gridVisible ? `${i * 80}ms` : "0ms",
          transitionProperty: "opacity, transform, border-color",
          transitionDuration: "700ms, 700ms, 300ms"
        }}>
              <div className="relative mx-auto mb-8 aspect-[4/5] w-full">
                <div className="relative h-full w-full overflow-hidden">
                  <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.02]" style={{
                transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
              }} />
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <span className="font-default text-xs font-semibold uppercase tracking-[0.25em]" style={{
              color: "hsl(0 0% 45%)"
            }}>
                  {service.number}
                </span>
                <Badge variant="outline" className="font-default text-[0.7rem] font-semibold uppercase border-transparent" style={{
              backgroundColor: "hsl(21 82% 51% / 0.1)",
              color: "hsl(21 82% 51%)",
              letterSpacing: "0.1em",
              padding: "4px 10px"
            }}>
                  {service.badge}
                </Badge>
              </div>

              <h3 className="font-default text-foreground mb-3" style={{
            fontSize: "1.75rem",
            lineHeight: "1.2",
            letterSpacing: "0.05em",
            fontWeight: 500
          }}>
                {service.name}{" "}
                <span className="italic" style={{
              color: "hsl(21 82% 51%)"
            }}>
                  {service.accent}
                </span>
              </h3>

              <p className="font-default text-base" style={{
            color: "hsl(0 0% 45%)",
            lineHeight: "1.6"
          }}>
                {service.ingredients}
              </p>
            </article>)}
        </div>

        <div aria-hidden="true" className="pointer-events-none mt-16 hidden md:flex justify-end">
          <span className="font-default italic" style={{
          fontSize: "1.1rem",
          fontWeight: 300,
          lineHeight: "1.2",
          color: "hsl(21 82% 51%)",
          transform: "rotate(-2deg)",
          display: "inline-block"
        }}>
            Strategy meets craft.
          </span>
        </div>
      </div>
    </section>;
}